import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BookMarked, Plus, Search, Heart, Copy, Trash2, History, Wand2, X, Save, Loader2,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { CinematicStage, StageHeader, GlassCard, Pill, EmptyState, Spinner, cx } from "@/components/shared";

const CATEGORIES = ["General", "Educational", "Story", "Marketing", "Documentary", "Kids", "Motivational"];

export default function PromptLibrary() {
  const navigate = useNavigate();
  const [prompts, setPrompts] = useState(null);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");
  const [favOnly, setFavOnly] = useState(false);
  const [editing, setEditing] = useState(null);
  const [versionsFor, setVersionsFor] = useState(null);

  const load = () => api.get("/prompts").then(({ data }) => setPrompts(data)).catch(() => setPrompts([]));
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!prompts) return [];
    let out = prompts;
    if (cat !== "All") out = out.filter((p) => p.category === cat);
    if (favOnly) out = out.filter((p) => p.favorite);
    if (q) out = out.filter((p) => (p.title + p.text).toLowerCase().includes(q.toLowerCase()));
    return out;
  }, [prompts, q, cat, favOnly]);

  const save = async (p) => {
    try {
      if (p.id) await api.put(`/prompts/${p.id}`, p);
      else await api.post("/prompts", p);
      toast.success("Prompt saved"); setEditing(null); load();
    } catch { toast.error("Failed to save"); }
  };
  const clone = async (id) => { await api.post(`/prompts/${id}/clone`); toast.success("Cloned"); load(); };
  const del = async (id) => { await api.delete(`/prompts/${id}`); toast.success("Deleted"); load(); };
  const toggleFav = (p) => save({ ...p, favorite: !p.favorite });

  if (!prompts) return <Spinner label="Loading Prompt Library…" />;

  return (
    <CinematicStage>
      <StageHeader stage="PRE" room="The Prompt Vault · Prompt Library" icon={BookMarked} title="Save your best ideas"
        subtitle="Reusable prompts with categories, favorites, cloning and version history."
        actions={<button onClick={() => setEditing({ title: "", text: "", category: "General" })} data-testid="prompt-new-button" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#5B4DFF] text-white font-semibold glow-purple"><Plus className="w-4 h-4" /> New Prompt</button>} />

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="flex items-center gap-2 flex-1 px-4 py-2.5 rounded-xl bg-white/5 border border-white/10">
          <Search className="w-4 h-4 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search prompts…" data-testid="prompt-search" className="bg-transparent outline-none text-sm w-full text-white placeholder:text-slate-500" />
        </div>
        <div className="flex gap-2 flex-wrap">
          {["All", ...CATEGORIES].map((c) => (
            <button key={c} onClick={() => setCat(c)} className={cx("px-3.5 py-2 rounded-xl text-sm border", cat === c ? "bg-[#5B4DFF]/25 border-[#5B4DFF]/60 text-white" : "bg-white/5 border-white/10 text-slate-300")}>{c}</button>
          ))}
          <button onClick={() => setFavOnly((v) => !v)} className={cx("px-3.5 py-2 rounded-xl text-sm border flex items-center gap-1.5", favOnly ? "bg-[#FFB300]/20 border-[#FFB300]/50 text-[#FFB300]" : "bg-white/5 border-white/10 text-slate-300")}><Heart className="w-3.5 h-3.5" /> Favorites</button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon={BookMarked} title="No prompts yet" subtitle="Save your first prompt to reuse it anytime." />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((p) => (
            <GlassCard key={p.id} hover className="p-5 flex flex-col" data-testid="prompt-card">
              <div className="flex items-start justify-between mb-2">
                <Pill tone="purple">{p.category}</Pill>
                <Heart onClick={() => toggleFav(p)} className={cx("w-4 h-4 cursor-pointer", p.favorite ? "fill-[#FFB300] text-[#FFB300]" : "text-slate-500")} />
              </div>
              <h3 className="font-display font-semibold text-white">{p.title}</h3>
              <p className="text-sm text-slate-400 mt-1 flex-1 line-clamp-3">{p.text}</p>
              <div className="flex items-center gap-1.5 mt-4">
                <button onClick={() => navigate("/create", { state: { prompt: p.text } })} className="flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-lg bg-[#5B4DFF]/20 border border-[#5B4DFF]/40 text-white text-xs" data-testid={`prompt-use-${p.id}`}><Wand2 className="w-3.5 h-3.5" /> Use</button>
                <IconBtn onClick={() => setEditing(p)} icon={Save} title="Edit" />
                <IconBtn onClick={() => clone(p.id)} icon={Copy} title="Clone" />
                <IconBtn onClick={() => setVersionsFor(p)} icon={History} title="Versions" />
                <IconBtn onClick={() => del(p.id)} icon={Trash2} title="Delete" danger />
              </div>
            </GlassCard>
          ))}
        </div>
      )}

      {editing && <PromptModal prompt={editing} onClose={() => setEditing(null)} onSave={save} />}
      {versionsFor && <VersionsModal prompt={versionsFor} onClose={() => setVersionsFor(null)} />}
    </CinematicStage>
  );
}

function IconBtn({ onClick, icon: Icon, title, danger }) {
  return <button onClick={onClick} title={title} className={cx("p-2 rounded-lg bg-white/5 border border-white/10", danger ? "text-slate-400 hover:text-red-400" : "text-slate-400 hover:text-white")}><Icon className="w-3.5 h-3.5" /></button>;
}

function PromptModal({ prompt, onClose, onSave }) {
  const [p, setP] = useState(prompt);
  const [saving, setSaving] = useState(false);
  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <GlassCard className="glass-strong p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4"><h3 className="font-display text-lg font-semibold">{p.id ? "Edit" : "New"} Prompt</h3><button onClick={onClose}><X className="w-5 h-5 text-slate-400" /></button></div>
        <input value={p.title} onChange={(e) => setP({ ...p, title: e.target.value })} placeholder="Title" data-testid="prompt-title-input" className="w-full mb-3 bg-white/5 border border-white/10 rounded-xl p-3 outline-none text-white text-sm" />
        <select value={p.category} onChange={(e) => setP({ ...p, category: e.target.value })} className="w-full mb-3 bg-white/5 border border-white/10 rounded-xl p-3 outline-none text-white text-sm">
          {CATEGORIES.map((c) => <option key={c} value={c} className="bg-[#0A1435]">{c}</option>)}
        </select>
        <textarea value={p.text} onChange={(e) => setP({ ...p, text: e.target.value })} rows={5} placeholder="Prompt text…" data-testid="prompt-text-input" className="w-full mb-4 bg-white/5 border border-white/10 rounded-xl p-3 outline-none text-white text-sm resize-none" />
        <button onClick={async () => { setSaving(true); await onSave(p); setSaving(false); }} disabled={!p.title || !p.text || saving} data-testid="prompt-save-button" className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-[#FFB300] text-[#081229] font-semibold disabled:opacity-50">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Prompt</button>
      </GlassCard>
    </div>
  );
}

function VersionsModal({ prompt, onClose }) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <GlassCard className="glass-strong p-6 w-full max-w-lg max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4"><h3 className="font-display text-lg font-semibold">Version History</h3><button onClick={onClose}><X className="w-5 h-5 text-slate-400" /></button></div>
        <div className="space-y-3">
          {(prompt.versions || []).slice().reverse().map((v, i) => (
            <div key={i} className="rounded-xl bg-white/5 border border-white/10 p-3">
              <div className="text-xs text-slate-500 mb-1 font-mono">{new Date(v.at).toLocaleString()}</div>
              <div className="text-sm text-slate-200">{v.text}</div>
            </div>
          ))}
        </div>
      </GlassCard>
    </div>
  );
}
